/**
 * Required single-select company field for a form that scopes work to ONE
 * company without creating a record in it — e.g. "which company's GL mappings
 * am I editing", a wizard step that picks a source company.
 *
 * - Hidden when the tenant has a single company (single value is implicit).
 * - The caller owns the value, the pre-fill and the submit guard
 *   (`disabled={!companyId}`); this component just enforces the visible prompt.
 *
 * **Not for a create form.** A new record's company follows the create-time
 * precedence contract (context > global scope > sole company,
 * docs/platform/multi-company-design.md §3) and its single implementation,
 * `useCompanyCreatePrecedence` + `CompanyCreateField` — which also renders the
 * inherited/pre-filled company as a chip instead of a bare picker. Do not
 * re-derive that chain around this component.
 */
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useMyCompanies } from '@/shared/users/hooks/useCompanies';

export interface CompanyFormFieldProps {
  /** Selected company id; empty string = no selection (form blocks submit). */
  value: string;
  onChange: (companyId: string) => void;
  /** Override the label (default: "Company"). */
  label?: string;
  /** Show an `*` after the label and `aria-required`. Default true. */
  required?: boolean;
  /** Whether the field is disabled. */
  disabled?: boolean;
  /** Field id for label association. */
  id?: string;
  className?: string;
}

export function CompanyFormField({
  value,
  onChange,
  label,
  required = true,
  disabled,
  id = 'company-field',
  className,
}: CompanyFormFieldProps) {
  const { t } = useTranslation(['user_management', 'common']);
  const { data, isLoading } = useMyCompanies();
  const companies = data?.data ?? [];

  if (companies.length <= 1) return null;

  const resolvedLabel = label ?? t('user_management:companies.fieldLabel', 'Company');

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={id} required={required}>
        {resolvedLabel}
      </Label>
      <Select
        value={value || ''}
        onValueChange={onChange}
        disabled={disabled || isLoading}
      >
        <SelectTrigger id={id} aria-required={required} aria-label={resolvedLabel}>
          <span className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
            <SelectValue
              placeholder={t(
                'user_management:companies.pickCompanyPlaceholder',
                'Select a company…',
              )}
            />
          </span>
        </SelectTrigger>
        <SelectContent>
          {companies.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              {c.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
